// ════════════════════════════════════════════════════════════════════════
//  Sitzung und Warteschlange
//
//  Der Übungscode liegt im sessionStorage, NICHT im localStorage: er stirbt mit
//  dem Tab. Auf einem Koffer-iPad wechseln die Kinder, und niemand soll die
//  Zugangsmarke des vorherigen im Browser finden.
//
//  Die Warteschlange muss im localStorage liegen (sie soll ein Neuladen
//  überleben) und enthält damit unvermeidlich den Code. Deshalb: TTL von 24 h,
//  und beim Abmelden wird sie gelöscht.
// ════════════════════════════════════════════════════════════════════════

import {
  BackendError,
  saveProgress,
  saveTest,
  type BackendConfig,
  type Course,
} from "../../../shared/lib/backend.js";

const CODE_KEY = "otto.code";
const COURSES_KEY = "otto.lehrgaenge";
const COURSE_KEY = "otto.lehrgang";
const QUEUE_KEY = "otto.queue.v1";
const TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Eine geübte Stufe, die noch zum Server muss.
 *
 * `kind` ist verpflichtend – im Speicher darf es trotzdem fehlen: Einträge einer
 * älteren Fassung, die beim Aktualisieren im localStorage liegen, haben es nicht
 * und sind geübte Stufen. `readQueue` trägt es nach, statt sie verfallen zu
 * lassen. Ein optionales Feld im Typ hätte den Unterschied dagegen in jede
 * Abfrage getragen.
 */
export type QueuedProgress = {
  readonly kind: "progress";
  readonly code: string;
  readonly lessonKey: string;
  readonly stageKey: string;
  readonly accuracy: number;
  /** Zeitpunkt der Übung in ms. Grundlage der TTL. */
  readonly time: number;
};

/**
 * Eine Messung, die noch zum Server muss.
 *
 * Sie gehört hier hinein und nicht in einen zweiten Speicher: eine Messung ist
 * NICHT wiederholbar – der Server nimmt je Phase nur die erste. Ginge sie im
 * Schul-WLAN verloren, fehlte sie für immer, und das Kind könnte den Test nicht
 * einfach noch einmal machen.
 */
export type QueuedTest = {
  readonly kind: "test";
  readonly code: string;
  readonly testKey: string;
  readonly phase: "vorher" | "nachher";
  readonly chars: number;
  readonly seconds: number;
  readonly accuracy: number;
  readonly time: number;
};

export type QueuedEntry = QueuedProgress | QueuedTest;

// ── Code ────────────────────────────────────────────────────────────────

export function readCode(): string | null {
  try {
    return globalThis.sessionStorage?.getItem(CODE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writeCode(code: string): void {
  try {
    globalThis.sessionStorage?.setItem(CODE_KEY, code);
  } catch {
    // Ohne Speicher gilt der Code nur für diese Ansicht. Kein Grund abzubrechen.
  }
}

// ── Lehrgänge ───────────────────────────────────────────────────────────
//
// Neben dem Code, im sessionStorage und aus demselben Grund: sie gehören zu
// diesem Kind und sollen mit dem Tab sterben.
//
// Die LISTE liegt mit dort, nicht nur die Wahl. Ohne sie stünde ein Kind nach
// einem Neuladen mit gültigem Code vor einem Lehrgang, den die App nicht kennt
// – und übte still mit der eingebackenen Fassung weiter.

function readJson<T>(key: string): T | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(key);
    return raw === null || raw === undefined ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export function readCourses(): readonly Course[] {
  const stored = readJson<Course[]>(COURSES_KEY);
  return Array.isArray(stored) ? stored : [];
}

export function writeCourses(courses: readonly Course[]): void {
  try {
    globalThis.sessionStorage?.setItem(COURSES_KEY, JSON.stringify(courses));
  } catch {
    // Siehe writeCode: ohne Speicher gilt es nur für diese Ansicht.
  }
}

/** Der gewählte Lehrgang, als Slug. Null heißt: das Kind hat noch nicht gewählt. */
export function readChosenCourse(): string | null {
  try {
    return globalThis.sessionStorage?.getItem(COURSE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writeChosenCourse(slug: string | null): void {
  try {
    if (slug === null) globalThis.sessionStorage?.removeItem(COURSE_KEY);
    else globalThis.sessionStorage?.setItem(COURSE_KEY, slug);
  } catch {
    /* siehe writeCode */
  }
}

/** Abmelden: Code, Lehrgänge und Warteschlange weg. Der „Fertig“-Knopf ruft das auf. */
export function endSession(): void {
  for (const [makeStore, queryKeys] of [
    [globalThis.sessionStorage, CODE_KEY],
    [globalThis.sessionStorage, COURSES_KEY],
    [globalThis.sessionStorage, COURSE_KEY],
    [globalThis.localStorage, QUEUE_KEY],
  ] as const) {
    try {
      makeStore?.removeItem(queryKeys);
    } catch {
      /* siehe setzeCode */
    }
  }
}

// ── Warteschlange ───────────────────────────────────────────────────────

/** Wie ein Eintrag im Speicher aussehen darf: `kind` fehlt bei alten. */
type StoredEntry = QueuedTest | (Omit<QueuedProgress, "kind"> & { kind?: "progress" });

function isEntry(value: unknown): value is StoredEntry {
  if (typeof value !== "object" || value === null) return false;
  const w = value as Record<string, unknown>;
  if (typeof w["code"] !== "string" || typeof w["time"] !== "number") return false;

  if (w["kind"] === "test") {
    return (
      typeof w["testKey"] === "string" &&
      (w["phase"] === "vorher" || w["phase"] === "nachher") &&
      typeof w["chars"] === "number" &&
      typeof w["seconds"] === "number" &&
      typeof w["accuracy"] === "number"
    );
  }

  return (
    typeof w["lessonKey"] === "string" &&
    typeof w["stageKey"] === "string" &&
    typeof w["accuracy"] === "number"
  );
}

/**
 * Liest die Warteschlange und wirft dabei alles weg, was älter als 24 h ist.
 *
 * Die TTL ist kein Aufräumen, sondern Datensparsamkeit: jeder Eintrag enthält
 * einen Übungscode. Was nach einem Tag nicht angekommen ist, wird niemand mehr
 * vermissen – der Fortschritt steht ohnehin lokal.
 */
export function readQueue(now: number = Date.now()): QueuedEntry[] {
  let raw: string | null = null;
  try {
    raw = globalThis.localStorage?.getItem(QUEUE_KEY) ?? null;
  } catch {
    return [];
  }
  if (raw === null) return [];

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isEntry)
      .map((e) => (e.kind === "test" ? e : { ...e, kind: "progress" as const }))
      .filter((e) => now - e.time < TTL_MS);
  } catch {
    return [];
  }
}

function writeQueue(entries: readonly QueuedEntry[]): void {
  try {
    if (entries.length === 0) globalThis.localStorage?.removeItem(QUEUE_KEY);
    else globalThis.localStorage?.setItem(QUEUE_KEY, JSON.stringify(entries));
  } catch {
    /* siehe setzeCode */
  }
}

export function enqueue(entry: QueuedEntry): void {
  // Eine Messung wird NICHT zusammengefasst: der Server behält je Phase die
  // erste, und zwei Messungen derselben Phase gibt es nur, wenn wirklich zweimal
  // gemessen wurde. Zusammenfassen hiesse hier, eine davon zu erfinden.
  if (entry.kind === "test") {
    writeQueue([...readQueue(entry.time), entry]);
    return;
  }

  // Gleiche Stufe zweimal: nur den besseren Wert behalten. Der Server macht mit
  // greatest() dasselbe – hier gespart wird ein Aufruf, nicht Richtigkeit.
  const existing = readQueue(entry.time).filter(
    (e) =>
      !(
        e.kind !== "test" &&
        e.code === entry.code &&
        e.stageKey === entry.stageKey &&
        e.accuracy <= entry.accuracy
      ),
  );
  writeQueue([...existing, entry]);
}

export type FlushResult = {
  readonly sent: number;
  readonly dropped: number;
  readonly remaining: number;
};

/**
 * Versucht, die Warteschlange loszuwerden.
 *
 * Abgelehnte Einträge (4xx) werden VERWORFEN, nicht wiederholt: ein falscher
 * Code oder eine abgelaufene Lizenz wird durch Wiederholung nicht richtig, und
 * ein 4xx endlos zu senden ist der klassische Fehler an dieser Stelle.
 */
export async function flushQueue(
  config: BackendConfig,
  fetchFn: typeof fetch = fetch,
  now: number = Date.now(),
): Promise<FlushResult> {
  const offen = readQueue(now);
  const remaining: QueuedEntry[] = [];
  let sent = 0;
  let dropped = 0;

  for (const entry of offen) {
    try {
      if (entry.kind === "test") await saveTest(config, entry, fetchFn);
      else await saveProgress(config, entry, fetchFn);
      sent += 1;
    } catch (error) {
      if (error instanceof BackendError && error.kind === "rejected") {
        dropped += 1;
        continue;
      }
      remaining.push(entry);
    }
  }

  writeQueue(remaining);
  return { sent, dropped, remaining: remaining.length };
}
